import { useState, useEffect } from 'react';
import { Modal, Box, Card, TextField, Button, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import intl from 'react-intl-universal';
import BasicTable from 'components/BasicTable';
import { searchColumns } from './SearchColumns';
import PropTypes from 'prop-types';
import { getUserFullName } from 'utils/name';

import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';

const propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sendApplication: PropTypes.func.isRequired,
  friendList: PropTypes.array.isRequired,
  currentUsername: PropTypes.string.isRequired,
  sentFriendApplicationList: PropTypes.array.isRequired,
};

const AddFriendModal = ({
  isOpened,
  onClose,
  sendApplication,
  friendList,
  currentUsername,
  sentFriendApplicationList,
}) => {
  const [searchedUserList, setSearchedUserList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const columns = searchColumns(sendApplication);

  const handleSearchKeywordChange = (event) => {
    setSearchKeyword(event.target.value);
  };

  const handleRefreshClick = () => {
    setRefreshTrigger(refreshTrigger + 1);
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
    });
  };

  const handleClickSearch = async () => {
    const apiVariables = {
      params: {
        organizationsId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        keyword: searchKeyword,
      },
    };
    const { users } = await api.get(
      `${DOLPHIN_API.SEARCH}/users`,
      apiVariables
    );

    const tableData = users
      .map((user, _) => {
        const isFriend = friendList.filter(
          (user_) => user_.userID === user.username
        );
        const isSent = sentFriendApplicationList.filter(
          (user_) => user_.toUserID === user.username
        );

        // If is friend or current user then remove from users
        if (isFriend.length > 0 || user.username === currentUsername) {
          return null;
        }
        return {
          username: getUserFullName(
            user.firstName,
            user.lastName,
            user.preferredLanguage
          ),
          userID: user.username,
          operation: {
            userId: user.username,
            isSent: isSent.length > 0, // To show that the user has been sent a friend request
          },
        };
      })
      .filter((user) => user !== null);
    setSearchedUserList(tableData);
  };

  useEffect(() => {
    handleClickSearch();
  }, [isOpened]);

  return (
    <Modal
      open={isOpened}
      onClose={onClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Card
        sx={{
          minWidth: '1000px',
          margin: '10px',
        }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
          aria-label="close">
          <CloseIcon />
        </IconButton>
        <Box>
          <Grid container spacing={2}>
            <Grid size={12}>
              <BasicTable
                columns={columns}
                data={searchedUserList}
                isLoading={false}
                darkMode={false}
                tableTitle={intl.get('friends_label_add_friend_modal_title')}
                rowsPerPageOptions={[10]}
                enableRowSelection={false}
                customToolbar={
                  <Grid container spacing={2}>
                    <Grid size={8}>
                      <TextField
                        variant="outlined"
                        value={searchKeyword}
                        sx={{ width: '100%' }}
                        size="small"
                        onChange={handleSearchKeywordChange}
                      />
                    </Grid>
                    <Grid size={4}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleClickSearch}>
                        {intl.get('friends_button_search')}
                      </Button>
                    </Grid>
                  </Grid>
                }
                handleRefreshClick={handleRefreshClick}
              />
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Modal>
  );
};

AddFriendModal.propTypes = propTypes;

export default AddFriendModal;
